<template>
  <div v-if="project" class="forms-elements">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />
    <ProjectTimeline :project="project" />
    <b-card-group deck class="project-cards-tight">
      <ProjectCardOverview :project="project" :projectNumber="projectNumberByClientContext" />
      <ProjectCardState :project="project" />
      <ProjectCardInfo :project="project" />
      <!-- <ProjectCardCommunication :project="project" /> -->
      <!-- <ProjectCardPosts :project="project" /> -->
    </b-card-group>

    <b-tabs ref="tabs" content-class="" fill v-model="tabIndex">
      <template v-if="isGuestTemplateVisible">
        <b-tab
          v-if="isCustomerTabVisible"
          ref="customer"
          data-key="customer"
          :title="$t('project_customer_tab')"
        >
          <ProjectCustomer :project="project" />
        </b-tab>

        <b-tab
          v-if="isCommunicationTabVisible"
          ref="communication"
          data-key="communication"
          :title="$t('communication')"
        >
          <ProjectCommunication :project="project" />
        </b-tab>

        <b-tab
          v-if="isGroupVisible(PROJECT_CONFIGURATION_TAB_GROUP)"
          ref="configuration"
          data-key="configuration"
          :title="$t('configuration')"
        >
          <ProjectSolarPlant :project="project" />
        </b-tab>

        <b-tab
          v-if="isTechnicalReviewTabVisible"
          ref="technicalReview"
          :title="$t('technicalReview')"
          data-key="technicalReview"
        >
          <ProjectTechnicalReview :project="project" />
        </b-tab>

        <b-tab
          v-if="isInstallationTabVisible"
          ref="installation"
          data-key="installation"
          :title="$t('installation')"
        >
          <ProjectInstallation :project="project" />
        </b-tab>

        <b-tab
          v-if="isDocumentsTabVisible"
          ref="documents"
          data-key="documents"
          :title="$t('documents')"
        >
          <ProjectDocumentsTab :project="project" />
        </b-tab>

        <b-tab v-if="isMeasurementReportTabVisible" :title="$t('measurement')">
          <ProjectReportTab :project="project" :protocol="MEASUREMENT_REPORT" />
        </b-tab>

        <b-tab v-if="isAcceptanceReportTabVisible" :title="$t('acceptanceReport')">
          <ProjectReportTab :project="project" :protocol="ACCEPTANCE_REPORT" />
        </b-tab>
        <b-tab v-if="isServiceReportTabVisible" :title="$t('serviceReport')">
          <ProjectReportTab :project="project" :protocol="SERVICE_REPORT" />
        </b-tab>

        <b-row>
          <b-col lg="12" md="12" sm="12">
            <ButtonBar
              :showPreviousButton="buttons.showPreviousButton"
              :showNextButton="buttons.showNextButton"
              :showDeleteButton="buttons.showDeleteButton"
              :model="project"
              modelEditRoute="ProjectEditPage"
              modelListRoute="ProjectsPaginatedPage"
              modelRouteParamName="projectNumber"
              :nextModelNumber="project.nextProjectNumber"
              :previousModelNumber="project.previousProjectNumber"
              :updateCallback="saveProjectPage"
              :createCallback="createProject"
              :deleteCallback="deleteProject"
              :fetchCallback="fetchProjects"
            />
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-tab
          v-if="isInternalTabVisible"
          ref="internal"
          data-key="internal"
          :title="$t('internal')"
        >
          <ProjectInternal :project="project" />
        </b-tab>

        <!-- data-key stays iabCalculation: it can be linked through the query projectTab -->
        <b-tab
          v-if="isGroupVisible(PROJECT_CALCULATION_TAB_GROUP)"
          ref="iabCalculation"
          data-key="iabCalculation"
          :title="$t('calculation')"
        >
          <ProjectCalculation :project="project" />
        </b-tab>

        <b-tab
          v-if="isCustomerTabVisible"
          ref="customer"
          data-key="customer"
          :title="$t('project_customer_tab')"
        >
          <ProjectCustomer :project="project" />
        </b-tab>

        <b-tab
          v-if="isCommunicationTabVisible"
          ref="communication"
          data-key="communication"
          :title="$t('communication')"
        >
          <ProjectCommunication :project="project" :selectedAppointment="selectedAppointment" />
        </b-tab>

        <b-tab
          v-if="isGroupVisible(PROJECT_CONFIGURATION_TAB_GROUP)"
          ref="configuration"
          data-key="configuration"
          :title="$t('configuration')"
        >
          <ProjectSolarPlant :project="project" />
        </b-tab>

        <b-tab
          v-if="isTechnicalReviewTabVisible"
          ref="technicalReview"
          :title="$t('technicalReview')"
          data-key="technicalReview"
        >
          <ProjectTechnicalReview :project="project" />
        </b-tab>

        <b-tab v-if="isMaterialTabVisible" ref="items" :title="$t('items')" data-key="items">
          <ProjectMaterials :project="project" />
        </b-tab>

        <b-tab
          v-if="isInstallationTabVisible"
          ref="installation"
          data-key="installation"
          :title="$t('installation')"
        >
          <ProjectInstallation :project="project" />
        </b-tab>

        <b-tab
          v-if="isPowerCompanyTabVisible"
          ref="powerCompany"
          data-key="powerCompany"
          :title="$t('powerCompany')"
        >
          <ProjectPowerCompanyTab :project="project" />
        </b-tab>

        <b-tab
          ref="registration"
          data-key="registration"
          v-if="isAdminOnlyLegacyTabVisible"
          :title="$t('registration')"
        >
          <ProjectRegistration :project="project" />
        </b-tab>

        <b-tab
          v-if="isAccountingTabVisible"
          ref="offers_invoices"
          data-key="offers_invoices"
          :title="$t('offers_invoices')"
        >
          <ProjectAccounting :project="project" />
        </b-tab>

        <b-tab
          ref="Freigabe"
          data-key="Freigabe"
          v-if="isAdminOnlyLegacyTabVisible"
          title="Freigabe"
        >
          <EmployerApproval :project="project" />
        </b-tab>

        <b-tab
          v-if="isDocumentsTabVisible"
          ref="documents"
          data-key="documents"
          :title="$t('documents')"
        >
          <ProjectDocumentsTab :project="project" />
        </b-tab>

        <b-tab v-if="isBankTabVisible" ref="bank" data-key="bank" :title="$t('bank')">
          <ProjectBankTab :project="project" />
        </b-tab>

        <b-tab
          v-if="isMeasurementReportTabVisible"
          ref="measurement"
          data-key="measurement"
          :title="$t('measurementReport')"
        >
          <ProjectReportTab :project="project" :protocol="MEASUREMENT_REPORT" />
        </b-tab>

        <b-tab
          v-if="isAcceptanceReportTabVisible"
          ref="acceptanceReport"
          data-key="acceptanceReport"
          :title="$t('acceptanceReport')"
        >
          <ProjectReportTab :project="project" :protocol="ACCEPTANCE_REPORT" />
        </b-tab>

        <b-tab
          v-if="isServiceReportTabVisible"
          ref="serviceReport"
          data-key="serviceReport"
          :title="$t('serviceReport')"
        >
          <ProjectReportTab :project="project" :protocol="SERVICE_REPORT" />
        </b-tab>

        <b-tab
          v-if="isQualityReportTabVisible"
          ref="qualityReport"
          data-key="qualityReport"
          :title="$t('qualityReport')"
        >
          <ProjectReportTab :project="project" :protocol="QUALITY_REPORT" />
        </b-tab>

        <b-tab v-if="isAdminOnlyLegacyTabVisible" :title="$t('assessmentReport')">
          <ProjectAssessmentReport
            :project="project"
            v-on:project-report-attachment-list:delete="onDeleteProjectAttachment"
          />
        </b-tab>

        <b-tab
          v-if="isSalesReportTabVisible"
          ref="salesReport"
          data-key="salesReport"
          :title="$t('salesReport')"
        >
          <ProjectReportTab :project="project" :protocol="SALES_REPORT" />
        </b-tab>

        <b-tab v-if="isAdminOnlyLegacyTabVisible && !isNew" :title="$t('audit_log')">
          <ProjectAuditLog :project="project" />
        </b-tab>

        <b-row>
          <b-col lg="12" md="12" sm="12">
            <ButtonBar
              :showPreviousButton="buttons.showPreviousButton"
              :showNextButton="buttons.showNextButton"
              :showDeleteButton="buttons.showDeleteButton"
              :model="project"
              modelEditRoute="ProjectEditPage"
              modelListRoute="ProjectsPaginatedPage"
              modelRouteParamName="projectNumber"
              :nextModelNumber="project.nextProjectNumber"
              :previousModelNumber="project.previousProjectNumber"
              :updateCallback="saveProjectPage"
              :createCallback="createProject"
              :deleteCallback="deleteProject"
              :fetchCallback="fetchProjects"
              :isReadOnly="isPageReadOnly"
            />
          </b-col>
        </b-row> </template
    ></b-tabs>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs';
import { mapGetters, mapActions } from 'vuex';
import { getYesNoStateColor, getProjectStateColor } from '@/helpers/colors';
import ProjectTechnicalReview from '@/views/Projects/ProjectTechnicalReview';
import ProjectCalculation from '@/views/Projects/ProjectCalculation';
import EmployerApproval from '@/views/Projects/EmployerApproval';
import ProjectReportTab from '@/views/Projects/ProjectReportTab';
import {
  ACCEPTANCE_REPORT,
  MEASUREMENT_REPORT,
  QUALITY_REPORT,
  SALES_REPORT,
  SERVICE_REPORT,
} from '@/blocks/project/report/reportProtocols';
import ProjectSolarPlant from '@/views/Projects/ProjectSolarPlant';
import ProjectInternal from '@/views/Projects/ProjectInternal';
import ProjectCustomer from '@/views/Projects/ProjectCustomer';
import ProjectAccounting from '@/views/Projects/ProjectAccounting';
import ProjectRegistration from '@/views/Projects/ProjectRegistration';
import ProjectCardOverview from '@/views/Projects/ProjectCardOverview';
// import ProjectCardCommunication from '@/views/Projects/ProjectCardCommunication';
import ProjectCardState from '@/views/Projects/ProjectCardState';
import ProjectDocumentsTab from '@/views/Projects/ProjectDocumentsTab';
import ProjectBankTab from '@/views/Projects/ProjectBankTab';
import ProjectInstallation from '@/views/Projects/ProjectInstallation';
import ProjectTimeline from '@/views/Projects/ProjectTimeline';
import ButtonBar from '@/components/ButtonBar.vue';
import { findTabIndexByKey } from '@/helpers/projectTabs';
import {
  PROJECT_ACCOUNTING_TAB_GROUP,
  PROJECT_CALCULATION_TAB_GROUP,
  PROJECT_COMMUNICATION_TAB_GROUP,
  PROJECT_CONFIGURATION_TAB_GROUP,
  PROJECT_CUSTOMER_TAB_GROUP,
  PROJECT_INSTALLATION_TAB_GROUP,
  PROJECT_INTERNAL_TAB_GROUP,
  PROJECT_MATERIAL_TAB_GROUP,
  PROJECT_POWER_COMPANY_TAB_GROUP,
  PROJECT_BANK_TAB_GROUP,
  PROJECT_DOCUMENTS_TAB_GROUP,
  PROJECT_MEASUREMENT_REPORT_TAB_GROUP,
  PROJECT_ACCEPTANCE_REPORT_TAB_GROUP,
  PROJECT_SERVICE_REPORT_TAB_GROUP,
  PROJECT_QUALITY_REPORT_TAB_GROUP,
  PROJECT_SALES_REPORT_TAB_GROUP,
  PROJECT_TECHNICAL_REVIEW_TAB_GROUP,
  PROJECTS_ACCESS_NODE,
} from '@/navigation/accessNodes';
import { coversAccessLevel } from '@/helpers/accessLevel';
import ProjectPowerCompanyTab from '@/views/Projects/ProjectPowerCompanyTab';
// import ProjectMaterial from '@/views/Projects/Items/ProjectMaterial';
import ProjectMaterials from '@/views/Projects/Items/ProjectMaterials';
import ProjectAssessmentReport from '@/views/Projects/ProjectAssessmentReport';
import ProjectCommunication from '@/views/Projects/ProjectCommunication';
import ProjectAuditLog from './ProjectAuditLog.vue';
// import ProjectCardPosts from '@/views/Projects/ProjectCardPosts';
import ProjectCardInfo from '@/views/Projects/ProjectCardInfo';

export default {
  name: 'ProjectEditPage',
  components: {
    Breadcrumbs,
    ProjectTechnicalReview,
    ProjectCalculation,
    EmployerApproval,
    ProjectReportTab,
    ProjectSolarPlant,
    ProjectInternal,
    ProjectCustomer,
    ProjectAccounting,
    ButtonBar,
    ProjectRegistration,
    ProjectCardOverview,
    // ProjectCardCommunication,
    ProjectCardState,
    ProjectDocumentsTab,
    ProjectBankTab,
    ProjectInstallation,
    ProjectTimeline,
    // ProjectMaterial,
    ProjectMaterials,
    ProjectAssessmentReport,
    ProjectAuditLog,
    ProjectCommunication,
    // ProjectCardPosts,
    ProjectPowerCompanyTab,
    ProjectCardInfo,
  },
  props: {
    projectNumber: {
      required: false,
      default: null,
    },
    projectTab: {
      type: String,
      required: false,
      default: '',
    },
    // referer: { default: '', type: String },
  },
  data() {
    return {
      number: null,
      user: {},
      buttonsDisabled: false,
      filteredProjectAttachmentTypes: [],
      tabIndex: 0,
      referer: null,
      selectedAppointment: null,
      PROJECT_CONFIGURATION_TAB_GROUP,
      PROJECT_CALCULATION_TAB_GROUP,
      PROJECT_INTERNAL_TAB_GROUP,
      MEASUREMENT_REPORT,
      ACCEPTANCE_REPORT,
      SERVICE_REPORT,
      QUALITY_REPORT,
      SALES_REPORT,
    };
  },
  filters: {
    toDate(value) {
      // console.log(value);
      return value ? new Date(value) : null;
    },
  },
  computed: {
    ...mapGetters('auth', [
      'isAdmin',
      'isClient',
      'getCurrentUser',
      'isGuest',
      'isStandardControlling',
      'isOrganizationRoleNavigationEnabled',
    ]),
    ...mapGetters([
      'getFlashMessage',
      'getProject',
      'getProjectByNumber',
      'getEnumValues',
      'isGroupVisible',
      'getAccessLevel',
    ]),

    isNew() {
      return this.project.id === '_new';
    },

    /** "Matrix-Authz kennt keine Gäste": every role gets the regular tabs by group (ADR 0004). */
    isGuestTemplateVisible() {
      return !this.isOrganizationRoleNavigationEnabled && this.isGuest;
    },

    /** Matrix-Authz: the neighbours are scoped by organization, the target page checks the level. */
    canUseNeighbourArrows() {
      return this.isOrganizationRoleNavigationEnabled || this.isAdmin;
    },

    isPageReadOnly() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return !coversAccessLevel(this.getAccessLevel(PROJECTS_ACCESS_NODE), 'editor');
      }
      return this.isStandardControlling;
    },

    buttons() {
      return {
        showPreviousButton:
          this.canUseNeighbourArrows && this.project && this.project.previousProjectNumber
            ? true
            : false,
        showNextButton:
          this.canUseNeighbourArrows && this.project && this.project.nextProjectNumber
            ? true
            : false,
        showDeleteButton: false,
        // this.isAdmin && this.project && this.project.number !== '_new' ? true : false,
      };
    },

    project() {
      return this.getProjectByNumber(this.number);
    },
    /**
     * Workaround for the legacy roles; remove when the project blocks of the role matrix take effect
     * (docs/work/_done/2026-10-first-block).
     */
    canSeeProjectInternalTabs() {
      return !this.isStandardControlling;
    },
    /**
     * Every section of Intern is a block: switch on, the group decides; switch off, the legacy
     * rule stays (plan docs/work/_done/2026-10-project-block-calculation-internal).
     */
    isInternalTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_INTERNAL_TAB_GROUP);
      }
      return this.canSeeProjectInternalTabs;
    },
    /**
     * Every section of Angebote/Rechnungen is a block or the legacy section: Matrix-Authz, the
     * group decides; Legacy-Authz, the legacy rule stays (see feature page
     * projekt-angebote-rechnungen).
     */
    isAccountingTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_ACCOUNTING_TAB_GROUP);
      }
      return this.canSeeProjectInternalTabs;
    },
    /**
     * Every section of Kunde / Geschäftspartner is a block: switch on, the group decides; switch
     * off, the tab is shown as before (plan
     * docs/work/_done/2026-10-project-block-customer-additional-information, decided 4).
     */
    isCustomerTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_CUSTOMER_TAB_GROUP);
      }
      return true;
    },
    /**
     * Every section of Kommunikation is a block: switch on, the group decides; switch off, the tab
     * is shown as before (see feature page projekt-kommunikation).
     */
    isCommunicationTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_COMMUNICATION_TAB_GROUP);
      }
      return true;
    },
    /**
     * Every section of Techn. Prüfung is a block: switch on, the group decides; switch off, the
     * tab is shown as before (see feature page projekt-technische-pruefung).
     */
    isTechnicalReviewTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_TECHNICAL_REVIEW_TAB_GROUP);
      }
      return true;
    },
    /**
     * Every section of Montage is a block: in Matrix-Authz the group of the tab decides; in
     * Legacy-Authz the tab is shown as before (see feature page projekt-montage).
     */
    isInstallationTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_INSTALLATION_TAB_GROUP);
      }
      return true;
    },
    /**
     * Every section of Material is a block: in Matrix-Authz the group of the tab decides; in
     * Legacy-Authz the tab is shown as before (see feature page projekt-material).
     */
    isMaterialTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_MATERIAL_TAB_GROUP);
      }
      return true;
    },
    /**
     * Every section of EVU is a block: in Matrix-Authz the group of the tab decides; in
     * Legacy-Authz the tab is shown as before (see feature page projekt-evu).
     */
    isPowerCompanyTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_POWER_COMPANY_TAB_GROUP);
      }
      return true;
    },
    /**
     * Every section of Bank is a block: in Matrix-Authz the group of the tab decides; in
     * Legacy-Authz the tab is shown as before (see feature page projekt-bank).
     */
    isBankTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_BANK_TAB_GROUP);
      }
      return true;
    },
    /**
     * Anmeldung, Freigabe, Gutachten and Aktivitätsprotokoll have no blocks yet: hidden in
     * Matrix-Authz until further notice, admins only in Legacy-Authz (see feature page index,
     * "Reiter der Projektseite").
     */
    isAdminOnlyLegacyTabVisible() {
      return !this.isOrganizationRoleNavigationEnabled && this.isAdmin;
    },
    usesEmployerAttachmentTypes() {
      return !this.isOrganizationRoleNavigationEnabled && this.isClient;
    },
    /**
     * The tab is one block: in Matrix-Authz its group decides in both templates of the page; in
     * Legacy-Authz the tab is shown as before, also for guests (see feature page projekt-dokumente).
     */
    isDocumentsTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_DOCUMENTS_TAB_GROUP);
      }
      return true;
    },
    /**
     * Matrix-Authz: the group decides in both templates of the page; Legacy-Authz: shown as before,
     * also for guests (see feature page projekt-protokolle).
     */
    isMeasurementReportTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_MEASUREMENT_REPORT_TAB_GROUP);
      }
      return true;
    },
    /** As the tab Aufmaßprotokoll (see feature page projekt-protokolle). */
    isAcceptanceReportTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_ACCEPTANCE_REPORT_TAB_GROUP);
      }
      return true;
    },
    /** As the tab Aufmaßprotokoll (see feature page projekt-protokolle). */
    isServiceReportTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_SERVICE_REPORT_TAB_GROUP);
      }
      return true;
    },
    /**
     * As the tab Aufmaßprotokoll; the guest template has no quality tab (see feature page
     * projekt-protokolle).
     */
    isQualityReportTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_QUALITY_REPORT_TAB_GROUP);
      }
      return true;
    },
    /**
     * Matrix-Authz by the group, the rule "not for guests" falls away there; Legacy-Authz as before
     * (the guest template has no sales tab, see feature page projekt-protokolle).
     */
    isSalesReportTabVisible() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return this.isGroupVisible(PROJECT_SALES_REPORT_TAB_GROUP);
      }
      return true;
    },

    projectNumberByClientContext() {
      let number = '';
      if (this.project.number === '_new') {
        number = this.$t('_new');
      }

      if (
        this.isOrganizationRoleNavigationEnabled &&
        this.project.number &&
        this.project.number !== '_new'
      ) {
        return this.project.number.toString().padStart(4, '0');
      }

      if (
        this.isClient &&
        this.project.number &&
        this.project.number !== '_new' &&
        this.project.clientProjectNumber
      ) {
        number = this.project.clientProjectNumber.toString().padStart(4, '0');
      }

      if (this.isAdmin && this.project.number && this.project.number !== '_new') {
        number = this.project.number.toString().padStart(4, '0');
      }
      return number;
    },
    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: 'Projekte', route: { name: 'ProjectsPaginatedPage' } },
        {
          name: this.projectNumberByClientContext,
        },
      ];
    },
  },
  methods: {
    ...mapActions([
      'initProject',
      'fetchProjects',
      'fetchProjectWithBlocksByNumber',
      'saveProjectPage',
      'createProject',
      'deleteProject',
      'fetchOriginalInstallationTypes',
      'uploadProjectAttachment',
      'deleteProjectAttachment',
      'fetchEnumValues',
      'fetchReportTemplateImages',
    ]),
    setActiveTab(key) {
      const renderedTabs = this.$refs.tabs
        .getTabs()
        .map((tab) => ({ key: tab.$attrs['data-key'] }));
      const tabIndex = findTabIndexByKey(renderedTabs, key);
      if (tabIndex === -1) {
        console.log(`Tab with key "${key}" not found.`);
        return;
      }
      this.tabIndex = tabIndex;
    },
    isDisabled(e) {
      e.preventDefault();
    },
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    projectStateColor(state) {
      return getProjectStateColor(state);
    },

    /** @todo changes to project are lost */
    async onDeleteProjectAttachment(event) {
      await this.deleteProjectAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
        attachmentType: event.attachmentType,
      });
    },

    async setup() {
      await this.fetchOriginalInstallationTypes();
      await this.fetchEnumValues('ProjectAttachmentType');
      await this.fetchEnumValues('EmployerProjectAttachmentType');
      // Only the tab Gutachten reads them from the store; the report blocks bring their own.
      if (this.isAdminOnlyLegacyTabVisible) {
        await this.fetchReportTemplateImages();
      }

      this.filteredProjectAttachmentTypes = this.usesEmployerAttachmentTypes
        ? this.getEnumValues('EmployerProjectAttachmentType')
        : this.getEnumValues('ProjectAttachmentType');
    },

    async setupClientContext() {},

    async setupAdminContext() {},
  },
  async mounted() {
    if (this.isClient) {
      await this.setupClientContext();
    }
    if (this.isAdmin) {
      await this.setupAdminContext();
    }
    await this.setup();

    if (!this.project && this.projectNumber) {
      this.number = this.projectNumber;
      await this.fetchProjectWithBlocksByNumber(this.number).then((errors) => {
        if (errors && errors.length > 0 && errors[0].extensions.code === 'FORBIDDEN') {
          this.$router.push('/403');
        }
      });
    } else {
      this.initProject();
      this.number = '_new';
    }

    if (!this.isOrganizationRoleNavigationEnabled && this.isClient) {
      this.project.client = this.getCurrentUser.client;
    }

    // tabs render after the project is set
    await this.$nextTick();

    /**
     * Go to installation tab if coming from RouteSchedulingPage
     */
    if (this.referer && this.referer.name === 'RouteSchedulingPage') {
      this.setActiveTab('installation');
    }
    if (this.projectTab) {
      this.setActiveTab(this.projectTab);
    }
  },

  /**
   * if only parameters of the route have changed the component will be reused
   * we have to update all the data before the route updates
   * @param to
   * @param from
   * @param next
   */
  async beforeRouteUpdate(to, from, next) {
    if (to.params.projectNumber && to.params.projectNumber !== this.projectNumber) {
      this.$store.commit('SET_PROJECT_DEFAULT_STATE');
      this.number = to.params.projectNumber;
      await this.fetchProjectWithBlocksByNumber(this.number);
      await this.setup();
      next();
    } else {
      next(false);
    }
  },

  /**
   * Save referer to current view
   */
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.referer = from;
    });
  },
};
</script>

<style scoped lang="scss">
.spinner {
  top: 4px;
  position: relative;
}

.project-cards-tight {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

.project-cards-tight .card {
  margin-right: 0.25rem;
  margin-left: 0.25rem;
}

.customer-card {
  max-width: 50rem;
}
.solar-plant-card {
  max-width: 35rem;
}
.calculations-card {
  max-width: 25rem;
}
.solar-plant-data-grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1em;
  row-gap: 0.7em;
}
.calculations-grid-container {
  display: grid;
  grid-template-columns: 5fr 1fr;
  column-gap: 1em;
  row-gap: 0.7em;
}
.form-grid-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container div {
  padding: 0.4em;
}
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container-left div {
  padding: 0.4em;
}
.form-grid-container-left .label {
  text-align: right;
}

.trash-installation-appointment {
  text-align: right;
}
.trash-installation-appointment i {
  cursor: pointer;
}
legend {
  position: relative;
}
.button-right {
  position: absolute;
  display: inline-block;
  right: 0;
  top: 2px;
}

li > .badge {
  font-size: 0.8em;
  padding: 3px 4px 4px 4px;
}
.ck-content {
  min-height: 80px;
}
</style>
